import { useMemo } from 'react';
import { ScheduleRequest } from '@weave/schema-gen-ts/dist/schemas/schedule/api/v2/api.pb';
import dayjs from 'dayjs';
import { useTranslation } from '@frontend/i18n';
import { ExtendedAppointment } from '@frontend/schedule-helpers';
import { useScheduleAppointmentInfoPanelShallowStore } from '../../../stores/schedule-appointment-info-panel.store';
import { ScheduleRequestList } from '../../../views/Calendar/EventsPanel/ScheduleActions/ScheduleRequests/ScheduleRequestList/ScheduleRequestList';
import { AppointmentList } from './appointment-list';
import { PanelHeader } from './panel-header';

const getAppointmentsRangeText = (selectedAppointments: ExtendedAppointment[]) => {
  if (!selectedAppointments.length) return '';

  const appointmentTimes = selectedAppointments
    .filter((appointment) => appointment.start && appointment.duration)
    .map((appointment) => {
      const startTime = dayjs(appointment.start);
      const endTime = startTime.add(appointment.duration || 0, 'minute');
      return { start: startTime, end: endTime };
    });

  if (appointmentTimes.length === 0) return '';

  const minStartTime = appointmentTimes.reduce((min, current) =>
    current.start.isBefore(min.start) ? current : min
  ).start;

  const maxEndTime = appointmentTimes.reduce((max, current) => (current.end.isAfter(max.end) ? current : max)).end;

  return `${minStartTime.format('h:mm A')} - ${maxEndTime.format('h:mm A')}`;
};

export const OverlappingAppointmentsPanel = () => {
  const { t } = useTranslation('schedule');
  const { selectedAppointments } = useScheduleAppointmentInfoPanelShallowStore('selectedAppointments');

  const providerName = selectedAppointments[0]?.practitionerName ?? '';
  const appointments = useMemo(
    () => selectedAppointments.filter((appt) => appt.type !== 'request'),
    [selectedAppointments]
  );
  const scheduleRequests = useMemo(
    () =>
      selectedAppointments.reduce((arr, event) => {
        if (event.type === 'request') arr.push(event as ScheduleRequest);
        return arr;
      }, [] as ScheduleRequest[]),
    [selectedAppointments]
  );

  const appointmentTimeRange = useMemo(() => getAppointmentsRangeText(appointments), [appointments]);
  const appointmentInfoText = t('{{count}} appointments • {{timeRange}}', {
    count: appointments.length,
    timeRange: appointmentTimeRange,
  });
  const scheduleRequestInfoText = t('{{count}} requests', { count: scheduleRequests.length });

  return (
    <section css={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
      {appointments.length > 0 && (
        <>
          <PanelHeader
            title={t('Overlapping Appointments')}
            appointmentsInfoText={appointmentInfoText}
            providerName={providerName}
          />
          <AppointmentList appointments={appointments} />
        </>
      )}
      {scheduleRequests.length > 0 && (
        <>
          <PanelHeader
            title={t('Overlapping Requests')}
            appointmentsInfoText={scheduleRequestInfoText}
            providerName={providerName}
          />
          <ScheduleRequestList scheduleRequests={scheduleRequests} />
        </>
      )}
    </section>
  );
};
